import type { MarketDetailTop5Item } from '~/api/market'
import { onBeforeUnmount, onMounted, readonly, ref } from 'vue'
import { useEnvApi } from './useEnvApi'

const MARKET_TOP5_SSE_PATH = '/api/v4/datahub/market-data/top5/sse'
const MARKET_TOP5_SSE_EVENT = 'snapshot'
const MARKET_TOP5_RECONNECT_DELAY_MS = 5_000
const MARKET_TOP5_PAYLOAD_KEYS = ['data', 'top5', 'items', 'list', 'result'] as const

type MarketTop5Record = Record<string, unknown>
type MarketTop5Message = Readonly<{ data: string }>

const isRecord = (value: unknown): value is MarketTop5Record => value !== null && typeof value === 'object'

/** 兼容数组及常见接口包装层，提取 SSE 推送的 Top5 列表。 */
export const parseMarketTop5SsePayload = (payload: unknown): MarketDetailTop5Item[] | null => {
  if (Array.isArray(payload)) {
    return payload.every(isRecord) ? (payload as MarketDetailTop5Item[]) : null
  }

  if (typeof payload === 'string') {
    try {
      return parseMarketTop5SsePayload(JSON.parse(payload))
    } catch {
      return null
    }
  }

  if (!isRecord(payload)) return null

  for (const key of MARKET_TOP5_PAYLOAD_KEYS) {
    const items = parseMarketTop5SsePayload(payload[key])
    if (items !== null) return items
  }

  return null
}

/** 通过 EventSource GET 长连接持续更新市场 Top5。 */
export const useMarketTop5Sse = (getInitialItems: () => readonly MarketDetailTop5Item[] | undefined) => {
  const api = useEnvApi()
  const marketTop5 = ref([...(getInitialItems() ?? [])])
  const isDestroyed = ref(false)

  let eventSource: EventSource | null = null
  let reconnectTimerId: ReturnType<typeof setTimeout> | null = null

  const sseUrl = new URL(MARKET_TOP5_SSE_PATH, api.v4).toString()

  const clearReconnectTimer = () => {
    if (reconnectTimerId === null) return
    clearTimeout(reconnectTimerId)
    reconnectTimerId = null
  }

  const closeEventSource = () => {
    eventSource?.close()
    eventSource = null
  }

  const scheduleReconnect = () => {
    if (isDestroyed.value || reconnectTimerId !== null) return

    reconnectTimerId = setTimeout(() => {
      reconnectTimerId = null
      connectMarketTop5Sse()
    }, MARKET_TOP5_RECONNECT_DELAY_MS)
  }

  const handleMarketTop5Message = (event: MarketTop5Message) => {
    const items = parseMarketTop5SsePayload(event.data)
    if (items !== null && items.length > 0) {
      marketTop5.value = items
    }
  }

  const connectMarketTop5Sse = () => {
    if (isDestroyed.value || eventSource !== null) return

    try {
      const source = new EventSource(sseUrl)
      eventSource = source
      source.addEventListener(MARKET_TOP5_SSE_EVENT, handleMarketTop5Message)
      source.addEventListener('message', handleMarketTop5Message)
      source.addEventListener('error', () => {
        if (eventSource !== source) return
        closeEventSource()
        scheduleReconnect()
      })
    } catch {
      scheduleReconnect()
    }
  }

  onMounted(() => {
    isDestroyed.value = false
    connectMarketTop5Sse()
  })

  onBeforeUnmount(() => {
    isDestroyed.value = true
    clearReconnectTimer()
    closeEventSource()
  })

  return {
    marketTop5: readonly(marketTop5),
  }
}
